import React, { useMemo } from 'react';
import Papaparse from 'papaparse';
import { Button, IconButton, Typography } from '@mui/material';
import { downloadCsv } from '../../reports/Reports.utils';
import {
  AccountStatus,
  DummyMember,
  GroupedMembers,
  Member,
  ProjectDetail,
} from '../../../types';
import { useAppSelector } from '../../../redux/hooks';
import {
  getAccountDummyMembers,
  getAccountDummyProjects,
  getDateFilterValueLocal,
  getSelectedAccount,
  getSelectedAccountMembers,
  getSelectedProject,
  getVisibleAccountDummyMembers,
  selectHiddenAccountMembers,
} from '../../../redux/account';

import { BudgetsReportType } from '../types';
import { DateTime } from 'luxon';
import {
  CustomIcon,
  IconName,
} from '../../../components/customIcon/CustomIcon';
import { getHeadcountChanges } from '../../../redux/headcountChanges';
import {
  HEADCOUNT_CHANGE_REPORT_KEYS,
  headcountChangesFieldOrder,
} from '../../../constants/marginReports';

interface CsvDownloadButtonProps {
  style?: 'default' | 'icon';
}

export const CsvDownloadButton = ({
  style = 'default',
}: CsvDownloadButtonProps) => {
  const accountMembers = useAppSelector(getSelectedAccountMembers);
  const account = useAppSelector(getSelectedAccount);
  const project = useAppSelector(getSelectedProject);
  const accountDummyProjects = useAppSelector(getAccountDummyProjects);
  const accountDummyMembers = useAppSelector(getAccountDummyMembers);
  const dateFilterValueLocal = useAppSelector(getDateFilterValueLocal);
  const headcountChanges = useAppSelector(getHeadcountChanges);

  const visibleAccountDummyMembers: DummyMember[] | undefined = useAppSelector(
    getVisibleAccountDummyMembers,
  );

  const hiddenAccountMembers = useAppSelector(selectHiddenAccountMembers);

  const { membersInTeams } = useMemo(() => {
    const presentview = project ? [project] : account?.projects;
    return (
      presentview?.reduce(
        (
          { membersInTeams }: { membersInTeams: number },
          project: ProjectDetail,
        ) => ({
          membersInTeams: membersInTeams + project.membersCount,
        }),
        { membersInTeams: 0 },
      ) ?? { membersInTeams: 0 }
    );
  }, [project, account]);

  const handleDownloadCSV = () => {
    const currentPeriod = dateFilterValueLocal
      ? Number(DateTime.fromISO(dateFilterValueLocal).toFormat('yyyyMM'))
      : Number(DateTime.now().toFormat('yyyyMM'));

    const operationalCostData = (account?.operationalCost &&
      [...account.operationalCost]
        .sort((a, b) => b.period - a.period)
        ?.find((data) => data.period <= currentPeriod)) || {
      period: currentPeriod,
      operationalCost: 11,
    };

    const BUDGET_REPORT_KEYS = {
      AccountName: 'Account Name',
      MemberName: 'Member Name',
      ProjectName: 'Project Name',
      Stream: 'Stream',
      Seniority: 'Seniority',
      GrossMargin: 'Gorss margin %',
      NetMargin: 'Net margin %',
      revenue: 'Revenue',
      projectedRevenue: 'Projected Revenue',
      PayRate: 'Pay Rate',
      PayRangeMin: 'Compensation Range Min',
      PayRangeMax: 'Compensation Range Max',
      PayRangeApprove: 'Compensation Range Approve',
      BillRate: 'Bill Rate',
      RateCard: 'Rate Card',
      Location: 'Location',
      Allocation: 'Allocation %',
    };

    const csvName = project
      ? project.name
      : account
      ? account.name
      : 'Margins-details';

    const transformMemberData = (member: BudgetsReportType) => ({
      memberName: member.memberName,
      projectName: member.projectName,
      stream: member.stream,
      seniority: member.seniority,
      billRate: member.billRate,
      grossMargin: member.grossMargin,
      netMargin: member.netMargin,
      projectedRevenue: member.projectedRevenue,
      pastRevenue: member.pastRevenue,
      payRate: member.payRate,
      rateCard: member.rateCard,
      payRangeMin: member.payRangeMin,
      payRangeMax: member.payRangeMax,
      payRangeApprove: member.payRangeApprove,
      location: member.location,
      allocation: member.allocation,
      compensationType: member.compensationType || 'hourly',
    });

    const data: BudgetsReportType[] = [];
    if (!accountMembers || account) {
      data.push(
        transformMemberData({
          accountName: account?.name,
          grossMargin: account?.grossMargin,
          netMargin: account?.netMargin,
          pastRevenue: account?.pastRevenue,
          projectedRevenue: account?.projectedRevenue,
        }),
      );
    }

    if (accountMembers.length > 0) {
      accountMembers?.forEach((projectDetails) => {
        if (!project || projectDetails?.projectId === project?.id) {
          const projectOfAccountMember = account?.projects.find(
            (project) => project.id === projectDetails.projectId,
          );
          data.push(
            transformMemberData({
              projectName: projectDetails.groupName,
              grossMargin: projectDetails.grossMargin,
              netMargin: projectDetails.netMargin,
              pastRevenue: projectOfAccountMember?.pastRevenue,
              projectedRevenue: projectOfAccountMember?.projectedRevenue,
            }),
          );
        }
      });

      accountMembers
        ?.flatMap((projectItem: GroupedMembers) =>
          projectItem.members
            .filter((member: Member) => !member.isHidden)
            .map((member: Member) => {
              const isNotRestricted = [
                member.grossMargin,
                member.netMargin,
                member.compensationRate,
                member.compensationRange?.max,
                member.compensationRange?.min,
              ].some((val) => typeof val === 'number');
              const projectOfAccountMember = account?.projects.find(
                (project) => project.id === projectItem.projectId,
              );

              data.push(
                transformMemberData({
                  projectName: projectItem?.groupName,
                  memberName: member?.fullName,
                  stream: member?.stream,
                  seniority: member?.seniority,
                  billRate: isNotRestricted ? member?.resource?.billRate : null,
                  payRate: isNotRestricted ? member?.compensationRate : null,
                  payRangeMin: member?.compensationRange?.min,
                  payRangeMax: member.compensationRange?.max,
                  payRangeApprove: member.compensationRange?.approvedMax,
                  rateCard: isNotRestricted
                    ? member?.companyRateCard?.billRate
                    : null,
                  grossMargin: isNotRestricted ? member?.grossMargin : null,
                  location: isNotRestricted ? member?.legalLocation : null,
                  allocation: isNotRestricted ? member?.allocation : null,
                  netMargin: isNotRestricted ? member?.netMargin : null,
                  pastRevenue: projectOfAccountMember?.pastRevenue,
                  projectedRevenue: projectOfAccountMember?.projectedRevenue,
                  compensationType: member.compensationType,
                }),
              );
            }),
        )
        .filter(Boolean);
    }

    if (accountDummyMembers?.length) {
      const projectIdMap = new Map();

      if (accountDummyProjects?.length)
        accountDummyProjects.forEach((dummyProject) => {
          projectIdMap.set(dummyProject.id, dummyProject);
        });

      accountMembers?.forEach((dummyProject) => {
        projectIdMap.set(dummyProject.projectId, dummyProject);
      });

      accountDummyMembers.forEach((member) => {
        if (!member.isHidden) {
          const projectDet = projectIdMap.get(member.projectId);
          const projectName = projectDet
            ? projectDet.name || projectDet.groupName
            : '';
          data.push(
            transformMemberData({
              projectName: projectName,
              memberName: `${member.seniority} ${member.stream} ${member.country}`,
              stream: member.stream,
              seniority: member.seniority,
              billRate: member.billRate,
              payRate: member.compensationRate,
              payRangeMin: member?.payRange[0],
              payRangeMax: member.payRange[1],
              grossMargin: member.grossMargin,
              netMargin: member.netMargin,
              projectedRevenue: projectDet?.projectedRevenue,
              pastRevenue: projectDet?.pastRevenue,
              rateCard: member.rateCard,
              location: member.country,
              allocation: member.allocation,
            }),
          );
        }
      });
    }

    if (!data) {
      return;
    }

    const reportData = data.map((row: BudgetsReportType) => ({
      [BUDGET_REPORT_KEYS.AccountName]: account?.name ?? '-',
      [BUDGET_REPORT_KEYS.MemberName]: row.memberName ?? '-',
      [BUDGET_REPORT_KEYS.ProjectName]: row.projectName,
      [BUDGET_REPORT_KEYS.Stream]: row.stream ?? '-',
      [BUDGET_REPORT_KEYS.Seniority]: row.seniority ?? '-',
      [BUDGET_REPORT_KEYS.GrossMargin]: row.grossMargin ?? '-',
      [BUDGET_REPORT_KEYS.NetMargin]: row.netMargin ?? '-',
      [BUDGET_REPORT_KEYS.projectedRevenue]: row.projectedRevenue
        ? Math.round(row.projectedRevenue)
        : '-',
      [BUDGET_REPORT_KEYS.revenue]: row.pastRevenue
        ? Math.round(row.pastRevenue)
        : '-',
      [BUDGET_REPORT_KEYS.BillRate]: row.billRate
        ? `${row.billRate} ${row.compensationType}`
        : '-',
      [BUDGET_REPORT_KEYS.RateCard]: row.rateCard
        ? `${row.rateCard} ${row.compensationType}`
        : '-',
      [BUDGET_REPORT_KEYS.PayRate]: row.payRate
        ? `${row.payRate} ${row.compensationType}`
        : '-',
      [BUDGET_REPORT_KEYS.PayRangeMin]: row.payRangeMin ?? '-',
      [BUDGET_REPORT_KEYS.PayRangeMax]: row.payRangeMax ?? '-',
      [BUDGET_REPORT_KEYS.PayRangeApprove]: row.payRangeApprove ?? '-',
      [BUDGET_REPORT_KEYS.Location]: row.location ?? '-',
      [BUDGET_REPORT_KEYS.Allocation]: row.allocation ?? '-',
    }));

    const headCountData = headcountChanges.map((headcount) => ({
      [HEADCOUNT_CHANGE_REPORT_KEYS.CountChange]:
        Math.abs(headcount.countChange) +
        (headcount.countChange > 0 ? ' Joining' : ' Leaving'),
      [HEADCOUNT_CHANGE_REPORT_KEYS.Date]: headcount.date,

      [HEADCOUNT_CHANGE_REPORT_KEYS.BlendedPayRate]: headcount.blendedPayRate,
      [HEADCOUNT_CHANGE_REPORT_KEYS.BlendedRate]: headcount.blendedRate,
      [HEADCOUNT_CHANGE_REPORT_KEYS.ProjectName]: account?.projects.find(
        (project) => headcount.projectId === project.id,
      )?.name,
    }));

    const parseOptions = {
      quotes: true,
      columns: Object.values(BUDGET_REPORT_KEYS),
    };

    const csvReportData = Papaparse.unparse(reportData, parseOptions);

    const parseHeadcountDataOptions = {
      quotes: true,
      columns: headcountChangesFieldOrder,
    };
    const csvHeadcountData = Papaparse.unparse(
      headCountData,
      parseHeadcountDataOptions,
    );

    const csvData = `${csvReportData}\n\n "Operational Cost", "${
      operationalCostData?.operationalCost + ' hourly'
    }" \n\n ${
      csvHeadcountData
        ? `Projected headcount \n\n "Current Headcount", ${
            (visibleAccountDummyMembers?.length
              ? membersInTeams + visibleAccountDummyMembers.length
              : membersInTeams) - hiddenAccountMembers?.length
          },\n ${csvHeadcountData}`
        : ''
    }`;
    const currentDate = DateTime.now().toFormat('yyyy-MM-dd');
    const csvFilename = `${csvName}-${currentDate}.csv`;
    downloadCsv(csvData, csvFilename);
  };

  if (style === 'icon') {
    return (
      <IconButton onClick={handleDownloadCSV}>
        <CustomIcon icon={IconName.FileDownload} sx={{ color: '#666666' }} />
      </IconButton>
    );
  }

  return (
    <Button
      variant="contained"
      size="small"
      disabled={account?.status === AccountStatus.Closed}
      onClick={() => handleDownloadCSV()}
    >
      <CustomIcon
        icon={IconName.Grid}
        sx={{
          color: account?.status === AccountStatus.Closed ? '' : '#fff',
          fontSize: '18px',
        }}
      />
      <Typography variant="body1" sx={{ marginLeft: 0.7 }}>
        Export
      </Typography>
    </Button>
  );
};
